import { useState, useEffect, useRef } from "react";

const ZOHO_FORM_ENDPOINT = `${process.env.REACT_APP_BACKEND_URL}/api/zoho-form`;

/**
 * Re-run <script> tags that came inside injected HTML (browsers skip scripts
 * added via innerHTML). Required for Zoho's Mandatory-check + reCAPTCHA logic.
 */
const executeInlineScripts = (container) => {
  const scripts = container.querySelectorAll("script");
  scripts.forEach((oldScript) => {
    const newScript = document.createElement("script");
    for (const attr of oldScript.attributes) {
      newScript.setAttribute(attr.name, attr.value);
    }
    newScript.textContent = oldScript.textContent;
    oldScript.parentNode.replaceChild(newScript, oldScript);
  });
};

const ErrorFallback = () => (
  <p
    className="text-sm"
    style={{ color: "var(--fg-muted)", marginTop: 12 }}
    data-testid="zoho-embed-error"
  >
    Форма тимчасово недоступна. Напишіть, будь ласка, на{" "}
    <a
      href="mailto:info@kadratalent.com"
      className="under-link"
      style={{ color: "var(--gold-500)" }}
    >
      info@kadratalent.com
    </a>
    .
  </p>
);

/**
 * Embeds a Zoho CRM WebForm by fetching pre-extracted HTML from our own
 * backend proxy (`/api/zoho-form`) and injecting it into the DOM.
 *
 * NOTE on innerHTML / XSS: the HTML comes exclusively from OUR own trusted
 * backend, which itself fetches from a hardcoded Zoho URL and parses out
 * `formContent`. No user input is ever spliced into this string. Sanitising
 * with DOMPurify is intentionally NOT used here because Zoho's <script>
 * blocks are required for form validation and reCAPTCHA — stripping them
 * would break the form entirely.
 */
export const ZohoEmbed = () => {
  const containerRef = useRef(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(ZOHO_FORM_ENDPOINT);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        if (!data.html) throw new Error("no html");
        // XSS note: `data.html` is produced ONLY by our own backend
        // (`/api/zoho-form`), which fetches from a hardcoded Zoho URL and
        // parses out `formContent`. No user input is ever spliced into
        // this string. Sanitising with DOMPurify would strip Zoho's
        // required <script> blocks for reCAPTCHA + validation.
        container.innerHTML = data.html;
        executeInlineScripts(container);
        setStatus("ready");
      } catch (err) {
        if (!cancelled) {
          if (process.env.NODE_ENV !== "production") {
            console.error("Zoho embed load failed:", err);
          }
          setStatus("error");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <div
        ref={containerRef}
        id="zoho-quote-form"
        data-testid="zoho-quote-form"
        data-status={status}
        className="zoho-embed"
      />
      {status === "error" && <ErrorFallback />}
    </>
  );
};
